import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, Popconfirm, message } from "antd";
import { SyncOutlined, SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import editIcon from "../../../Assets/Icons/editTable.svg";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getGeofenceData } from "../../../API/User/geofence";
import { syncGeofence } from "../../../API/Admin/sytemConfiguration";

const Geofences = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geofenceData, setGeofenceData] = useState([]);

  const [messageApi, contextHolder] = message.useMessage();

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const successNotification = (msg) => {
    messageApi.success(msg);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  // const handleDelete = (key) => {
  //   setCustomerData((prev) => prev.filter((item) => item.key !== key));
  // };
  // const handleClick = () => {
  //   navigate("/add-geofences");
  // };

  // const handleEdit = () => {
  //   navigate("/view-geofences");
  // };

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getGeofenceData();

      if (Math.floor(response?.status / 100) === 2) {
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.geofences || [];

        setGeofenceData(arr);
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (err) {
      messageApi.error("Failed to fetch geofence.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const geofenceColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Geofence Name",
      dataIndex: "geofence_name",
      key: "geofence_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("geofence_name"),
    },
    {
      title: "Geofence Type",
      dataIndex: "geofence_type",
      key: "geofence_type",
      filters: [
        { text: "Circle", value: "circle" },
        { text: "Polygon", value: "polygon" },
        { text: "Sausage", value: "sausage" },
      ],
      onFilter: (value, record) => record.geofence_type === value,
      render: (type) => (
        <span className={`type-badge ${type}`}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </span>
      ),
    },
    {
      title: "Vehicle Count",
      dataIndex: "vehicle_count",
      key: "vehicle_count",
      sorter: (a, b) => a.vehicle_count - b.vehicle_count,
    },

    // {
    //   title: "Action",
    //   key: "action",
    //   render: (_, record) => (
    //     <div style={{ display: "flex", gap: "13px" }}>
    //       <a onClick={handleEdit}>
    //         <img src={editIcon} alt="Edit" />
    //       </a>
    //       <Popconfirm
    //         title="Are you sure to delete this?"
    //         icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
    //         okText="Yes"
    //         cancelText="Cancel"
    //         onConfirm={() => handleDelete(record.key)}
    //       >
    //         <a>
    //           <img src={delIcon} alt="Delete" />
    //         </a>
    //       </Popconfirm>
    //     </div>
    //   ),
    // },
  ];

  const handleSyncGeofence = async () => {
    try {
      setLoading(true);
      const res = await syncGeofence();

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(res.data.message || "Geofences synced successfully");
      } else {
        messageApi.error(res.data.message || "Failed to sync geofences");
      }
    } catch (err) {
      messageApi.error(
        err.response?.data?.message || "Error syncing geofences",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="geofences">
      {contextHolder}
      <div className="sub-heading">
        Manage all user-defined geofences (Points of Interest) used for security
        monitoring and automated alerts.
      </div>

      <Button
        icon={<SyncOutlined />}
        size="large"
        className="sync-btn"
        onClick={handleSyncGeofence}
        loading={loading}
      >
        Force Geofence Sync
      </Button>
      {/* <div className="button">
        <Button type="default" onClick={handleClick} className="add-btn">
          <img src={addIcon} alt="add user" />
          New Geofences
        </Button>
      </div> */}
      <div className="table">
        <GenericTable dataSource={geofenceData} columns={geofenceColumns} />
      </div>
    </div>
  );
};

export default Geofences;
